import { __ } from '@wordpress/i18n';
import apiFetch from '@wordpress/api-fetch';
import { store as noticesStore } from '@wordpress/notices';
import { useEffect, useState } from '@wordpress/element';
import { useDispatch } from '@wordpress/data';
import { validateUrl, validateClientSecret } from '../utils/validators';

const useSettings = () => {
	const [ appUrl, setAppUrl ] = useState();
	const [ dataUrl, setDataUrl ] = useState();
	const [ clientSecret, setClientSecret ] = useState();
	const [ dynamicParentPages, setDynamicParentPages ] = useState( [] );
	const [ renderingMethod, setRenderingMethod ] = useState( 'inline' );
	const [ shortcodeRenderingMethod, setShortcodeRenderingMethod ] = useState( 'default' );
	const [ useTestData, setUseTestData ] = useState( false );
	const [ listingLeadsIntegration, setListingLeadsIntegration ] = useState( false );
	const [ featuredImage, setFeaturedImage ] = useState();
	const [ themeColor, setThemeColor ] = useState( window.linear_admin_obj.theme_default_color || '#ed6a40' );
	const [ resultsPerPage, setResultsPerPage ] = useState( 4 );
	const [ customCss, setCustomCss ] = useState( '' );
	const [ customCssSrc, setCustomCssSrc ] = useState( '' );
	const [ additionalSettings, setAdditionalSettings ] = useState( [] );

	const nonce = window.linear_admin_obj.rest.nonce;

	const {
		removeAllNotices,
		createSuccessNotice,
		createInfoNotice,
		createErrorNotice,
	} = useDispatch( noticesStore );
	const apiUrl = window.linear_admin_obj.rest.endpoints.options;

	if ( ! apiUrl || ! nonce ) {
		return;
	}

	useEffect( () => {
		apiFetch( {
			path: apiUrl,
			method: 'GET',
			headers: {
				'X-WP-Nonce': nonce,
			},
		} )
			.then( ( response ) => {
				const parsedOptions = response.options;

				setAppUrl( parsedOptions.app_url || '' );
				setDataUrl( parsedOptions.data_url || '' );
				setClientSecret( parsedOptions.client_secret || '' );
				setDynamicParentPages(
					parsedOptions.dynamic_parent_pages || []
				);
				setRenderingMethod(
					parsedOptions.rendering_method || 'inline'
				);
				setShortcodeRenderingMethod(
					parsedOptions.shortcode_rendering_method || 'default'
				);
				setUseTestData( parsedOptions.use_test_data || false );
				setListingLeadsIntegration(
					parsedOptions.listing_leads_integration || false
				);
				setFeaturedImage( parsedOptions.featured_image || '' );
				setThemeColor(
					parsedOptions.theme_color || '#ed6a40'
				);
				setResultsPerPage(
					parsedOptions.results_per_page || 4
				);
				setCustomCss( parsedOptions.custom_css || '' );
				setCustomCssSrc( parsedOptions.custom_css_src || '' );
				setAdditionalSettings( parsedOptions.additional_settings || [] );
			} )
			.catch( ( error ) => {
				console.log( error );
				if ( error.code === 'rest_no_route' ) {
					createErrorNotice(
						__(
							"The REST API route is not set. Please check for plugin updates, and if that doesn't help, contact Linear support.",
							'linear'
						)
					);
				} else if ( error.code === 'rest_cannot_access' ) {
					createErrorNotice(
						__(
							'You do not have permission to access the REST API. Could there be something stopping this access?',
							'linear'
						)
					);
				} else if ( error.data && error.data.status === 500 ) {
					createErrorNotice(
						__(
							'The REST API encountered an internal server error. Please check if the WordPress Rest API is enabled.',
							'linear'
						)
					);
				} else {
					createErrorNotice(
						__(
							'Failed to fetch settings. Please try refreshing this page to see if that helps.',
							'linear'
						)
					);
				}
			} );
	}, [] );

	const saveSettings = () => {
		removeAllNotices();
		createInfoNotice( __( 'Saving settings...', 'linear' ) );

		const savingAppUrl = appUrl;
		const savingDataUrl = dataUrl;
		const savingClientSecret = clientSecret;

		return apiFetch( {
			path: apiUrl,
			method: 'POST',
			headers: {
				'X-WP-Nonce': nonce,
			},
			data: {
				app_url: appUrl,
				data_url: dataUrl,
				client_secret: clientSecret,
				dynamic_parent_pages: dynamicParentPages,
				rendering_method: renderingMethod,
				shortcode_rendering_method: shortcodeRenderingMethod,
				use_test_data: useTestData,
				listing_leads_integration: listingLeadsIntegration,
				featured_image: featuredImage,
				theme_color: themeColor,
				results_per_page: resultsPerPage,
				custom_css: customCss,
				custom_css_src: customCssSrc,
				additional_settings: additionalSettings
			},
		} )
			.then( () => {
				removeAllNotices();
				if (
					! validateUrl( savingAppUrl ) ||
					! validateUrl( savingDataUrl ) ||
					! validateClientSecret( savingClientSecret )
				) {
					createErrorNotice(
						__(
							'Settings saved, but some of the values are not valid. You may hence expect potential issues with the listings.',
							'linear'
						)
					);
				} else {
					createSuccessNotice( __( 'Settings saved.', 'linear' ) );
				}
			} )
			.catch( ( error ) => {
				removeAllNotices();
				console.log( error );
				if ( error.code === 'rest_no_route' ) {
					createErrorNotice(
						__(
							'The REST API route is not set. Please check the plugin configuration.',
							'linear'
						)
					);
				} else if ( error.code === 'rest_cannot_access' ) {
					createErrorNotice(
						__(
							'You do not have permission to access the REST API.',
							'linear'
						)
					);
				} else if ( error.data && error.data.status === 403 ) {
					createErrorNotice(
						__(
							'The REST API encountered an permission error. Please check if the REST API is enabled.',
							'linear'
						)
					);
				} else if ( error.data && error.data.status === 500 ) {
					createErrorNotice(
						__(
							'The REST API encountered an internal server error. Please check if the REST API is enabled.',
							'linear'
						)
					);
				} else {
					createErrorNotice(
						__(
							'Failed to save settings. Please try refreshing this page to see if that helps.',
							'linear'
						)
					);
				}
			} );
	};

	return {
		appUrl,
		setAppUrl,
		dataUrl,
		setDataUrl,
		clientSecret,
		setClientSecret,
		dynamicParentPages,
		setDynamicParentPages,
		renderingMethod,
		setRenderingMethod,
		shortcodeRenderingMethod,
		setShortcodeRenderingMethod,
		useTestData,
		setUseTestData,
		listingLeadsIntegration,
		setListingLeadsIntegration,
		featuredImage,
		setFeaturedImage,
		themeColor,
		setThemeColor,
		resultsPerPage,
		setResultsPerPage,
		customCss,
		setCustomCss,
		customCssSrc,
		setCustomCssSrc,
		additionalSettings,
		setAdditionalSettings,
		
		saveSettings,
	};
};

export default useSettings;
